import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isDespia } from "@/lib/isDespia";

const AppleCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Apple sends data via POST (form-data) or GET (query params)
      // For POST, we need to read from the request body which isn't directly available
      // The backend should have redirected with query params
      
      let code = searchParams.get('code');
      let idToken = searchParams.get('id_token');
      const errorParam = searchParams.get('error');

      // Check for Apple error
      if (errorParam) {
        console.error('[AppleCallback] Apple returned error:', errorParam);
        setError(`Erreur Apple: ${errorParam}`);
        toast.error("Erreur lors de la connexion avec Apple");
        setTimeout(() => navigate('/auth', { replace: true }), 2000);
        return;
      }

      // If no code/id_token in URL, check if this is a POST redirect
      // Apple POSTs to the callback, so we might need to handle form data
      if (!code && !idToken) {
        // Try to get from hash fragment (some OAuth flows use this)
        const hashParams = new URLSearchParams(location.hash.replace('#', ''));
        code = hashParams.get('code');
        idToken = hashParams.get('id_token');
      }

      if (!code || !idToken) {
        console.error('[AppleCallback] Missing code or id_token');
        setError("Paramètres de connexion manquants");
        toast.error("Erreur: paramètres de connexion manquants");
        setTimeout(() => navigate('/auth', { replace: true }), 2000);
        return;
      }

      console.log('[AppleCallback] Processing Apple callback');

      try {
        const redirectUri = `${window.location.origin}/callback/apple`;

        // Call our backend function to exchange the code
        const { data, error: fnError } = await supabase.functions.invoke('apple-auth-callback', {
          body: { code, id_token: idToken, redirect_uri: redirectUri },
        });

        if (fnError) {
          throw fnError;
        }

        if (!data?.access_token || !data?.refresh_token) {
          throw new Error('No tokens received from server');
        }

        console.log('[AppleCallback] Tokens received, setting session');

        // For Despia native app, redirect with tokens
        if (isDespia()) {
          const deeplink = `easyfinder://auth?access_token=${encodeURIComponent(data.access_token)}&refresh_token=${encodeURIComponent(data.refresh_token)}`;
          console.log('[AppleCallback] Redirecting to native app');
          window.location.href = deeplink;
          return;
        }

        // For web, set the session directly
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });

        if (sessionError) {
          throw sessionError;
        }

        toast.success("Connexion réussie !");
        navigate('/documents', { replace: true });

      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        console.error('[AppleCallback] Error:', message);
        setError(message);
        toast.error(`Erreur de connexion: ${message}`);
        setTimeout(() => navigate('/auth', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [searchParams, location, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <p className="text-muted-foreground text-sm">Redirection vers la page de connexion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <span className="text-sm">Connexion avec Apple en cours...</span>
      </div>
    </div>
  );
};

export default AppleCallback;
